import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

const DENOMS = [20, 50, 100, 200, 500, 1000];

export default function Denominations() {
  const [active, setActive] = useState(1000);
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/dashboard/denomination/${active}`).then((r) => setData(r.data)); }, [active]);

  const chart = data ? [
    { label: "Paper", pcf: data.avg_pcf_paper_kgco2e, fill: "#B45309" },
    { label: "Polymer", pcf: data.avg_pcf_polymer_kgco2e, fill: "#166534" },
  ].filter((d) => d.pcf > 0) : [];

  return (
    <div data-testid="denominations-page" className="space-y-6">
      <div>
        <div className="overline text-emerald-800">Denomination Dashboards</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">By Banknote</h1>
        <p className="text-sm text-slate-600 mt-2">Drill into each denomination's carbon profile and substrate transition status.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {DENOMS.map((d) => (
          <button
            key={d}
            data-testid={`denom-${d}`}
            onClick={() => setActive(d)}
            className={`px-5 py-3 text-sm font-mono font-semibold border transition-colors ${
              active === d ? "bg-emerald-800 text-white border-emerald-800" : "border-slate-300 hover:bg-slate-900 hover:text-white"
            }`}
          >₱{d}</button>
        ))}
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="border border-slate-200 p-6">
            <div className="overline text-slate-500">Total Batches</div>
            <div className="font-heading text-4xl font-extrabold text-slate-900 mt-2">{data.batches_count}</div>
            <div className="text-xs text-slate-500 mt-2">Paper: {data.paper_batches} · Polymer: {data.polymer_batches}</div>
          </div>
          <div className="border border-slate-200 p-6">
            <div className="overline text-slate-500">Avg PCF (Paper)</div>
            <div className="font-heading text-4xl font-extrabold text-amber-700 mt-2">{data.avg_pcf_paper_kgco2e}</div>
            <div className="text-xs text-slate-500 mt-2">kgCO₂e per accepted note</div>
          </div>
          <div className="border border-slate-200 p-6">
            <div className="overline text-slate-500">Avg PCF (Polymer)</div>
            <div className="font-heading text-4xl font-extrabold text-emerald-800 mt-2">{data.avg_pcf_polymer_kgco2e}</div>
            <div className="text-xs text-slate-500 mt-2">kgCO₂e per accepted note</div>
          </div>
        </div>
      )}

      {chart.length > 0 && (
        <div className="border border-slate-200 p-6 bg-white">
          <div className="overline text-slate-500">Substrate comparison</div>
          <div className="font-heading text-xl font-bold text-slate-900">₱{active} — Paper vs Polymer (kgCO₂e/note)</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="pcf">
                  {chart.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="border border-slate-200 p-6 bg-white overflow-x-auto">
        <div className="overline text-emerald-800 mb-3">Batches in this denomination</div>
        <table className="w-full text-sm" data-testid="denom-batches-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-500">Batch</th>
              <th className="px-3 py-2 overline text-slate-500">Substrate</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">tCO₂e</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">kg/note</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">Spoilage</th>
              <th className="px-3 py-2 overline text-slate-500">Hotspot</th>
            </tr>
          </thead>
          <tbody>
            {(data?.batches || []).map((b) => (
              <tr key={b.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono text-slate-900">{b.batch_code}</td>
                <td className="px-3 py-2 capitalize">{b.substrate}</td>
                <td className="px-3 py-2 text-right font-mono">{b.total_pcf_tco2e?.toFixed(3)}</td>
                <td className="px-3 py-2 text-right font-mono">{b.pcf_per_note_kgco2e?.toFixed(5)}</td>
                <td className="px-3 py-2 text-right font-mono">{b.spoilage_rate?.toFixed(2)}%</td>
                <td className="px-3 py-2 text-xs">{b.main_hotspot}</td>
              </tr>
            ))}
            {!data?.batches?.length && <tr><td colSpan="6" className="px-3 py-6 text-center text-slate-500">No batches.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
